import { Inject, Injectable } from '@angular/core';
import { defer, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, take } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import {
    AppNotificationService,
    AppNotificationType,
    ArrayModel,
    ConfigInstanceToken,
    ConfigInterface,
    CSE_STOCK,
    CseStockService,
    LoadingScreenComponent,
    MaterialModel,
    MaterialStockModel,
    ModalClass,
    NotificationModel,
    OdataMaterialService,
    SynchronizationService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import ConfirmationMaterialStockModel from '../../confirmation/confirmation-material-consume/confirmation-material-stock/confirmation-material-stock.model';
import { ConfirmationMaterialStockComponent } from '../../confirmation/confirmation-material-consume/confirmation-material-stock/confirmation-material-stock.component';

@Injectable({
    providedIn: 'root',
})
export class MaterialStockService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ConfigInstanceToken) protected readonly configService: ConfigInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly modalService: BsModalService,
        private readonly synchronizationService: SynchronizationService,
        private readonly cseStockService: CseStockService,
        private readonly odataMaterialService: OdataMaterialService,
    ) {}

    public triggerBootStock(
        notification: NotificationModel | undefined,
        listMaterial: ArrayModel<MaterialModel>,
        loadingScreenInstance?: BsModalRef<LoadingScreenComponent>,
    ): Observable<void | undefined> {
        return this.loadBootStock(notification, listMaterial).pipe(
            map((material) => {
                this.modalService.show(ConfirmationMaterialStockComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_MEDIUM,
                    initialState: {
                        isBootStockFromSideMenu: true,
                        model: new ConfirmationMaterialStockModel(listMaterial, notification, material),
                    },
                    ariaLabelledBy: 'confirmation-material-stock-modal-title',
                });
            }),
            catchError((error) => {
                this.log.error(`MaterialStockService : openBootStockUI: Error `, error);
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('MATERIAL_REQUEST_ERROR_TITLE'),
                    <string>this.translate.instant('MATERIAL_REQUEST_ERROR_BODY'),
                );

                return of(undefined);
            }),
            finalize(() => {
                if (loadingScreenInstance) {
                    loadingScreenInstance.hide();
                }
            }),
        );
    }

    /**
     * Loads the boot stock from MatStockAvailabilitySet. A failed sync is tolerated so the
     * last cached boot stock is still shown while offline.
     */
    public loadBootStock(
        notification: NotificationModel | undefined,
        listMaterial: ArrayModel<MaterialModel>,
    ): Observable<MaterialModel[]> {
        return defer(() => this.synchronizationService.receiveBootStock()).pipe(
            take(1),
            catchError((error) => {
                this.log.debug(`MaterialStockService: loadBootStock: receiveBootStock failed with error `, error);
                return of(undefined);
            }),
            mergeMap(() => this.cseStockService.updateBootStock()),
            mergeMap(async (materialStock: MaterialStockModel) =>
                this.cseStockService.createCseStockMaterialModelsAsync(
                    materialStock,
                    notification,
                    listMaterial,
                    CSE_STOCK.BOOT_STOCK,
                ),
            ),
        );
    }

    public triggerMaterialStock(notification: NotificationModel, listMaterial: ArrayModel<MaterialModel>): void {
        this.modalService.show(ConfirmationMaterialStockComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                isBootStockFromSideMenu: false,
                model: new ConfirmationMaterialStockModel(listMaterial, notification),
            },
            ariaLabelledBy: 'confirmation-material-stock-modal-title',
        });
    }

    public searchMaterialStock(
        stockModel: ConfirmationMaterialStockModel,
    ): Observable<[MaterialModel[], MaterialModel[]]> {
        const bootStockMaterials$ = this.loadBootStock(stockModel.notification, stockModel.consumptionList);

        const frontId = this.configService.getUserFrontId();
        const otherStockMaterials$ = this.odataMaterialService
            .getMaterialStock(frontId, stockModel.materialNumber.value, stockModel.storageLocation.value)
            .pipe(
                take(1),
                mergeMap((materialStockResponse) =>
                    this.cseStockService.createCseStockMaterialModelsAsync(
                        materialStockResponse,
                        stockModel.notification,
                        stockModel.consumptionList,
                        CSE_STOCK.OTHER_STOCK,
                    ),
                ),
            );

        return forkJoin([bootStockMaterials$, otherStockMaterials$]);
    }

    public checkIfMaterialExistsInBootStock(
        otherStock: MaterialModel[],
        bootStock: MaterialModel[],
    ): Observable<boolean> {
        const materialExistsInBootStock: MaterialModel[] = [];
        bootStock.forEach((bootstock) => {
            const match = otherStock.find(
                (material) =>
                    material.materialIdentifier.materialNumber.value ===
                        bootstock.materialIdentifier.materialNumber.value &&
                    material.storageLocation.value === bootstock.storageLocation.value,
            );
            if (match) {
                materialExistsInBootStock.push(match);
            }
        });
        if (materialExistsInBootStock.length > 0) {
            return this.showOtherStockWarning(materialExistsInBootStock);
        } else {
            return of(true);
        }
    }

    private showOtherStockWarning(materials: MaterialModel[]): Observable<boolean> {
        const materialList = materials.map((material) => material.materialIdentifier.materialNumber.value).join('<br>');
        const modalRef = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('CONFIRMATION_MATERIAL_BOOT_STOCK_WARNING_TITLE'),
                    <string>this.translate.instant('CONFIRMATION_MATERIAL_BOOT_STOCK_WARNING_BODY', {
                        materials: materialList,
                    }),
                    <string>this.translate.instant('CONFIRMATION_MATERIAL_BOOT_STOCK_WARNING_PICK_OTHER_STOCK'),
                    <string>this.translate.instant('CONFIRMATION_MATERIAL_BOOT_STOCK_WARNING_SWITCH_TO_BOOT_STOCK'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (modalRef.content?.modalResult ?? of(false)).pipe(
            take(1),
            finalize(() => {
                modalRef.hide();
            }),
        );
    }
}
